import React, { useCallback } from 'react';
import { HashLink } from 'react-router-hash-link';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import PropTypes from 'prop-types';
import { useDataLayer } from 'hooks/useDataLayer';
import { Text, Link } from 'roo-ui/components';
import {
  CONTACT_US,
  CONTACT_US_FORM,
  CONTACT_US_LIVE_CHAT,
  QH_SELF_SERVICE_URL,
  QH_TERMS_AND_CONDITIONS_URL,
  QCOM_LANDING_PAGE_URL,
  QH_PACKAGES_URL,
} from 'config';
import AppLink from 'components/AppLink';

const StyledText = styled(Text)`
  text-decoration: underline;
  color: ${themeGet('colors.brand.primary')};
  font-size: ${(props) => (props.fontSize ? props.fontSize : themeGet('fontSizes.base'))};
`;

export const ContactUsLink = ({ text, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleContactUsLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Customer Service Team Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link as={AppLink} to={CONTACT_US} onClick={handleContactUsLinkClick}>
      <StyledText>{text}</StyledText>
    </Link>
  );
};

ContactUsLink.propTypes = {
  text: PropTypes.string.isRequired,
  questionGroup: PropTypes.string.isRequired,
};

export const LiveChatLink = ({ text, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleLiveChatLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Livechat Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link as={HashLink} to={CONTACT_US_LIVE_CHAT} onClick={handleLiveChatLinkClick}>
      <StyledText>{text}</StyledText>
    </Link>
  );
};

LiveChatLink.propTypes = {
  text: PropTypes.string,
  questionGroup: PropTypes.string.isRequired,
};

LiveChatLink.defaultProps = {
  text: 'live chat',
};

export const TermsAndConditionsLink = ({ questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleTermsAndConditionsLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Terms And Conditions Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link underline href={QH_TERMS_AND_CONDITIONS_URL} onClick={handleTermsAndConditionsLinkClick}>
      <StyledText>terms and conditions</StyledText>
    </Link>
  );
};

TermsAndConditionsLink.propTypes = {
  questionGroup: PropTypes.string.isRequired,
};

export const QHHomePageLink = ({ text, fontSize, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleContactUsLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'QH Website Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link href={QCOM_LANDING_PAGE_URL} onClick={handleContactUsLinkClick} aria-label="Qantas Hotels website">
      <StyledText fontSize={fontSize}>{text}</StyledText>
    </Link>
  );
};

QHHomePageLink.propTypes = {
  text: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
  questionGroup: PropTypes.string.isRequired,
};

QHHomePageLink.defaultProps = {
  fontSize: 'base',
};

export const QHPackagesPageLink = ({ text, fontSize, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handlePackagesLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'QH Packages Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link href={QH_PACKAGES_URL} onClick={handlePackagesLinkClick} aria-label="Qantas Hotels fly and stay website">
      <StyledText fontSize={fontSize}>{text}</StyledText>
    </Link>
  );
};

QHPackagesPageLink.propTypes = {
  text: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
  questionGroup: PropTypes.string.isRequired,
};

QHPackagesPageLink.defaultProps = {
  fontSize: 'base',
};

export const SelfServiceLink = ({ text, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleQHSupportLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Self Service Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link href={`${QH_SELF_SERVICE_URL}?source=QH-faqs-page`} onClick={handleQHSupportLinkClick}>
      <StyledText>{text}</StyledText>
    </Link>
  );
};

SelfServiceLink.propTypes = {
  text: PropTypes.string.isRequired,
  questionGroup: PropTypes.string.isRequired,
};

export const QHSupportLink = ({ text, questionGroup }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleQHSupportLinkClick = useCallback(() => {
    emitInteractionEvent({ type: 'Feedback Form Link', value: `${questionGroup} Link Selected` });
  }, [emitInteractionEvent, questionGroup]);

  return (
    <Link as={AppLink} to={CONTACT_US_FORM} onClick={handleQHSupportLinkClick}>
      <StyledText>{text}</StyledText>
    </Link>
  );
};

QHSupportLink.propTypes = {
  text: PropTypes.string,
  questionGroup: PropTypes.string.isRequired,
};

QHSupportLink.defaultProps = {
  text: 'Qantas Hotels contacts',
};
